import React from 'react';
import {
  Box,
  Button,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from '@gamiphy/react-i18n';
import { observer } from 'mobx-react';
import { Controller, useForm } from 'react-hook-form';

import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { isAxiosError } from 'axios';
import { BRAZIL_PHONE_VALIDATION_REGEX } from '@gamiphy/loyalty/loyaltyPage/components/cashbackProgram/verification';
import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';

const PhoneForm: React.FC<{ onDone: (phone: string) => void }> = ({ onDone }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    setError,
    formState: {
      isSubmitting,
    },
    control,
  } = useForm<{ phone: string }>();

  const [unavowedPopupOpen, setUnavowedPopupOpen] = React.useState(false);

  const onSubmit = React.useCallback(handleSubmit(async (data) => {
    if (!UserStore.isRealUser && !AppStore.isCashbackProgram) {
      setUnavowedPopupOpen(true);
    } else {
      try {
        await UserStore.updatePhone(`+55${data.phone}`);
        onDone(`+55${data.phone}`);
      } catch (e) {
        if (isAxiosError(e)) {
          switch (e.response?.status) {
            case 409:
              setError('phone', { message: t('cashbackPopup.phone.exists', 'Phone number linked with another account.') });
              break;
            default:
              throw e;
          }
        }
      }
    }
  }), [UserStore.isRealUser, AppStore.isCashbackProgram]);

  return (
    <Box width="100%" component="form" onSubmit={onSubmit}>
      <InputLabel htmlFor="phone" sx={{ mb: 0.5 }}>
        <Typography fontSize={14} fontWeight={500} color="#000">
          <Trans
            i18nKey="user.name.phoneNumber"
            defaults="Phone Number"
          />
        </Typography>
      </InputLabel>
      <Controller
        name="phone"
        control={control}
        rules={{
          required: {
            value: true,
            message: t('validation.required', { defaultValue: 'This field is required' }),
          },
          pattern: {
            value: BRAZIL_PHONE_VALIDATION_REGEX,
            message: t('coreloyalty.cashback.phone.invalidDigits', 'Please enter a valid 10 or 11-digit phone number.'),
          },
        }}
        render={({
          fieldState: { error },
          field: {
            value,
            onChange,
          },
        }) => (
          <TextField
            color="primary"
            error={!!error}
            id="phone"
            fullWidth
            helperText={error?.message}
            FormHelperTextProps={{
              sx: {
                fontSize: 14,
              },
            }}
            value={value ?? ''}
            onChange={onChange}
            sx={{
              input: {
                color: '#000',
                textAlign: 'left !important',
                fontSize: 16,
                px: 1,
              },
            }}
            InputProps={{
              inputProps: {
                inputMode: 'numeric',
              },
              sx: {
                height: 44,
                p: 0,
                overflow: 'hidden',
              },
              startAdornment: (
                <Box height="100%" bgcolor="#F9F9F9" display="flex" alignItems="center" px={1} py={0.5} border="1px solid #D9DBDF">
                  <Typography fontSize={14} fontWeight={400} color="#525D6A" lineHeight={1}>
                    +55
                  </Typography>
                </Box>
              ),
            }}
          />
        )}
      />
      <Button
        variant="contained"
        fullWidth
        type="submit"
        disabled={isSubmitting}
        sx={{
          fontSize: 16,
          mt: 1.5,
        }}
      >
        <Trans
          i18nKey="survey.submit"
          defaults="Submit"
        />
      </Button>
      <UnavowedPopup
        open={unavowedPopupOpen}
        onClose={() => setUnavowedPopupOpen(false)}
        title={t('welcomeGift.signup', 'Sign up now to unlock an exclusive welcome gift!')}
      />
    </Box>
  );
};

export default observer(PhoneForm);
